import ko from "knockout";
import $ from "jquery";
import tinymce, { Editor, RawEditorOptions } from "@lifecare-welfare/tinymce-enterprise";
import config from "flow/config";
import baseUrl from "flow/wrappers/baseUrl";
import session from "flow/session/session";
import register from "flow/knockout/register";
import { TinyMCEProductionKey } from "3pp/tinyLicenseKeys";
import { getTinyCulture } from "flow/localization/tinyCulture";
import { ITinyMCELightweightKnockoutWrapper } from "flow/interfaces/ITinyMCELightweightKnockoutWrapper";

function createWrapper(
    element: HTMLElement,
    configObs: KnockoutObservable<RawEditorOptions>,
    textObs: KnockoutObservable<string>
): ITinyMCELightweightKnockoutWrapper {
    let myEditor: Editor;
    let originalOptions: RawEditorOptions;
    let originalText = "";
    const isDirty = ko.observable(false);

    configObs.subscribe(function (newConfig) {
        if (newConfig !== undefined) {
            create(newConfig);
        }
    });

    textObs.subscribe(function (newText) {
        if (newText !== getText()) {
            setText(newText);
        }
    });

    function commit() {
        myEditor.isNotDirty = true; // Force not dirty state
        isDirty(false);
        originalText = getText();
    }

    function onEditorInitialized(editor: Editor) {
        myEditor = editor;
        setText(textObs());

        myEditor.on("change keyup nodechange", function () {
            isDirty(myEditor.isDirty());
            textObs(getText());
        });

        myEditor.on("click", session.registerActivity);
        myEditor.on("wheel", session.registerActivity);
        myEditor.on("keyup", session.registerActivity);
        myEditor.on("touchend", session.registerActivity);

        myEditor.formatter.register("myselectionformat", {
            inline: "span",
            classes: "lc-editor-selected-text"
        });

        if (originalOptions.init_instance_callback !== undefined) {
            originalOptions.init_instance_callback(editor);
        }
    }

    function onEditorSetup(editor: Editor) {
        if (originalOptions.setup != undefined) {
            originalOptions.setup(editor);
        }
    }

    function editorHasBeenInitialized() {
        return myEditor !== undefined;
    }

    function create(options: RawEditorOptions) {
        if (options === undefined || options === null) {
            return;
        }

        originalText = textObs();

        originalOptions = {
            init_instance_callback: options.init_instance_callback,
            setup: options.setup
        };

        const overrides = {
            init_instance_callback: onEditorInitialized,
            setup: onEditorSetup
        };

        const newOptions = Object.assign(
            {
                language: getTinyCulture(),
                license_key: TinyMCEProductionKey,
                target: element,
                base_url: baseUrl + "dist"
            },
            options,
            overrides
        );

        // Support multiple create on the same element
        destroyEditorIfAny();

        tinymce.init(newOptions);

        // To prevent a memory leak, ensure that the underlying element's disposal destroys it's associated editor.
        ko.utils.domNodeDisposal.addDisposeCallback(element, function () {
            destroyEditorIfAny();
        });
    }

    function destroyEditorIfAny() {
        if (myEditor) {
            myEditor.off("click", session.registerActivity);
            myEditor.off("wheel", session.registerActivity);
            myEditor.off("keyup", session.registerActivity);
            myEditor.off("touchend", session.registerActivity);

            myEditor.remove();
            myEditor = undefined;
        }
    }

    function setText(text: string) {
        if (text === undefined || text === null) {
            text = "";
        }

        if (editorHasBeenInitialized()) {
            originalText = text;

            myEditor.setContent(text);
            myEditor.undoManager.clear();
            myEditor.isNotDirty = true; // Force not dirty state
            isDirty(false);
        }
    }

    function getText() {
        if (editorHasBeenInitialized()) {
            const text = myEditor.getContent();

            return text;
        }
        return "";
    }

    function revert() {
        if (editorHasBeenInitialized()) {
            textObs(originalText);
            myEditor.isNotDirty = true; // Force not dirty state
            isDirty(false);
        }
    }

    create(configObs());

    return {
        revert,
        commit,
        isDirty
    };
}

register.registerKnockoutBinding("inlineEditor", {
    init: function (element, valueAccessor, allBindingsAccessor) {
        const text = valueAccessor();

        // Assume we are working inside a form, so take parents current height as min height
        const parentHeight = $(element).parent().height();
        $(element).css("min-height", parentHeight);

        const bindingConfig: RawEditorOptions = {
            inline: true,
            schema: "html5-strict",
            entity_encoding: "named+numeric",
            menubar: false,
            plugins:
                "fullscreen lists pagebreak searchreplace visualblocks visualchars wordcount advlist autolink hr anchor insertdatetime save table contextmenu directionality textpattern image imagetools print",
            toolbar1:
                "pagebreak | cut copy paste | undo redo | searchreplace | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify",
            toolbar2:
                "fontfamily fontsize | bullist numlist | outdent indent blockquote | visualblocks visualchars fullscreen | ltr rtl | forecolor backcolor",
            statusbar: false,
            content_css: "/WE.Flow.Html/dist/lc_editor.css",
            body_class: "lc-editor-body",
            indent_before: "",
            indent_after: "",
            browser_spellcheck: true,
            pagebreak_separator: config.pagebreakSeparator
        };

        let extendedBindinConfig = bindingConfig;

        if (allBindingsAccessor().config) {
            extendedBindinConfig = $.extend({}, bindingConfig, allBindingsAccessor().config());
        }

        const configObs = ko.observable<RawEditorOptions>(extendedBindinConfig);

        const textObs = text;
        createWrapper(element, configObs, textObs);
    }
});

register.registerKnockoutBinding("readonlyInlineEditor", {
    init: function (element, valueAccessor) {
        const text = valueAccessor();

        // Assume we are working inside a form, so take parents current height as min height
        const parentHeight = $(element).parent().height();
        $(element).css("min-height", parentHeight);

        const configObs = ko.observable<RawEditorOptions>({
            inline: true,
            readonly: true,
            schema: "html5-strict",
            entity_encoding: "named+numeric",
            menubar: false,
            plugins: "pagebreak",
            toolbar: false,
            statusbar: false,
            content_css: "/WESE.FC.ProfessionalWeb/Content/editor.css",
            body_class: "lc-fc-editor-body",
            indent_before: "",
            indent_after: "",
            browser_spellcheck: false,
            pagebreak_separator: config.pagebreakSeparator
        });

        const textObs = text;

        createWrapper(element, configObs, textObs);
    }
});
